/* eslint-disable import/order */
import App from '@/App.vue';
import layoutsPlugin from '@/plugins/layouts';
import { setupDraggableDialogs } from '@/plugins/draggable-dialog';
import vuetify from '@/plugins/vuetify';
import { loadFonts } from '@/plugins/webfontloader';
import router from '@/router';
import { restoreAuthState } from '@/utils/auth-hydration';
import { initCrossTabSessionWatcher } from '@/utils/cross-tab-session';
import { initSessionMonitor } from '@/utils/session-monitor';
import { allowedText, decimalLimit, hasPermission, mobileNumber } from '@/utils/directives';
import { initVersionChecker } from '@/utils/version-checker';
import '@core/scss/template/index.scss';
import '@styles/styles.scss';
import { createPinia } from 'pinia';
import { createApp } from 'vue';
import 'vue-tel-input/vue-tel-input.css';
import Vue3Toastify, { type ToastContainerOptions } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';

/* Custom Filters */
import { formatDate, formatDay, formatMonth, formatYear } from '@/filters';

import { VDataTable, VDataTableServer } from 'vuetify/components';

loadFonts();

const googleMapsKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
if (googleMapsKey) {
  const script = document.createElement('script');

  script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsKey}&libraries=places&loading=async&callback=googleMapCallback`;
  document.head.appendChild(script);
}

// Create vue app
const app = createApp(App);

// Use plugins
app.use(vuetify);
app.use(createPinia());

// Restore auth state from localStorage (only if user was logged in)
restoreAuthState();

// Force this tab to log out if another tab signs in as a different account
initCrossTabSessionWatcher();

// Reload when a new build is deployed so an open tab doesn't freeze on missing chunk files
initVersionChecker();

// Validate the remembered session now and whenever the app comes back from idle/sleep/offline
initSessionMonitor();

app.use(router);
app.use(layoutsPlugin);
app.use(Vue3Toastify, {
  autoClose: 3000,
  theme: 'auto',
  transition: 'slide'
} as ToastContainerOptions);

app.component('VDataTableServer', VDataTableServer);
app.component('VDataTable', VDataTable);

app.config.globalProperties.$filters = {
  formatDay,
  formatMonth,
  formatYear,
  formatDate
};

app.directive('mobile-number', mobileNumber);
app.directive('allowed-text', allowedText);
app.directive('has-permission', hasPermission);
app.directive('decimal-limit', decimalLimit);

document.addEventListener('keydown', e => {
  const target = e.target as HTMLElement | null;

  if (target instanceof HTMLInputElement && target.type === 'number' && (e.key === 'e' || e.key === 'E')) {
    e.preventDefault();
  }
});

setupDraggableDialogs();

// Mount vue app
app.mount('#app');
